import React from 'react';
import { Link, useLocation, Navigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import {
  CheckCircle2,
  XCircle,
  Package,
  Truck,
  Home,
  ArrowRight,
  ArrowLeft,
  Mail,
  Phone,
  CreditCard,
  ShieldCheck,
  AlertTriangle,
} from 'lucide-react';
import { CONTACT_INFO, BRAND_INFO, CURRENCY } from '../data/mock';
import usePageTitle from '../hooks/usePageTitle';

/**
 * OrderConfirmationPage
 * --------------------------------------------------------------
 * Renders either an APPROVED or DECLINED state based on the NMI
 * response passed via router state from CheckoutPage:
 *   {
 *     success: boolean,
 *     transactionId: string,
 *     authCode: string,
 *     responseCode: string,
 *     responseText: string,
 *     orderId: string,
 *     amount: number,
 *     email: string,
 *   }
 *
 * If a user lands here directly (no state) we send them back to /shop.
 */
const OrderConfirmationPage = () => {
  const { state } = useLocation();
  const success = state?.success;

  // Hooks MUST run unconditionally on every render (rules of hooks).
  // Call usePageTitle here BEFORE any early return below.
  usePageTitle(
    success
      ? 'Order Confirmed'
      : success === false
        ? 'Payment Declined'
        : 'Order Status',
    success
      ? 'Your order has been successfully placed. Thank you for shopping with Stitch Your Dash.'
      : 'Your payment could not be processed. Please try again with a different card.'
  );

  // Direct nav / refresh => no result to show, route them away.
  if (!state || typeof state.success === 'undefined') {
    return <Navigate to="/shop" replace />;
  }

  const {
    transactionId,
    authCode,
    responseCode,
    responseText,
    orderId,
    amount,
    email,
  } = state;

  const fmtAmount =
    typeof amount === 'number' ? `$${amount.toFixed(2)} ${CURRENCY}` : null;

  // ============================================================
  // DECLINED VIEW
  // ============================================================
  if (!success) {
    return (
      <div className="min-h-screen bg-warmWhite-100">
        <Header />
        <main className="pt-[80px] md:pt-[125px] lg:pt-[140px] pb-20">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-8">
                <XCircle className="w-12 h-12 text-red-600" />
              </div>
              <h1 className="font-display text-3xl text-warmBrown-900 mb-3">
                Payment Declined
              </h1>
              <p className="text-warmBrown-600 text-lg mb-2">
                We couldn&apos;t process your payment.
              </p>
              <p className="text-warmBrown-500 mb-8">
                No charge was made. Your cart has been preserved so you can try
                again with a different card.
              </p>
            </div>

            {/* Decline reason banner */}
            <div className="bg-red-50 border border-red-200 rounded-3xl p-6 mb-8">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-red-800 mb-1">
                    Reason from payment processor
                  </p>
                  <p className="text-red-700 text-sm leading-relaxed">
                    {responseText || 'Payment was declined.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Payment Details (shown for both approve & decline so the
                customer can reference them if contacting support) */}
            <div className="bg-white p-6 md:p-8 rounded-3xl shadow-card border border-cream-200 mb-8">
              <div className="flex items-center gap-2 mb-5">
                <CreditCard className="w-5 h-5 text-warmBrown-500" />
                <h2 className="font-display text-lg text-warmBrown-900">
                  Transaction Details
                </h2>
              </div>
              <dl className="divide-y divide-cream-100">
                {orderId && (
                  <DetailRow label="Order Reference" value={orderId} mono />
                )}
                {transactionId && (
                  <DetailRow
                    label="NMI Transaction ID"
                    value={transactionId}
                    mono
                  />
                )}
                {responseCode && (
                  <DetailRow
                    label="Response Code"
                    value={`${responseCode}${responseText ? ` — ${responseText}` : ''}`}
                  />
                )}
                {fmtAmount && (
                  <DetailRow label="Attempted Amount" value={fmtAmount} />
                )}
                <DetailRow label="Status" value="DECLINED" pillVariant="error" />
              </dl>
            </div>

            {/* What to try next */}
            <div className="bg-cream-50 border border-cream-200 rounded-3xl p-6 mb-8">
              <h3 className="font-semibold text-warmBrown-900 mb-3">
                What you can try next
              </h3>
              <ul className="space-y-2 text-sm text-warmBrown-700">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500 font-bold">•</span>
                  Double-check the card number, expiration date and CVV.
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500 font-bold">•</span>
                  Make sure the billing address matches what your card issuer
                  has on file.
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500 font-bold">•</span>
                  Try a different card (Visa, Mastercard, or American Express —
                  Discover is not accepted).
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500 font-bold">•</span>
                  If the issue persists, contact your bank — they sometimes
                  block first-time online purchases.
                </li>
              </ul>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 mb-10">
              <Link
                to="/checkout"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-2xl transition-colors shadow-lg shadow-rose-600/20"
              >
                <ArrowLeft className="w-5 h-5" />
                Try Payment Again
              </Link>
              <Link
                to="/shop"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-white border border-cream-300 hover:border-rose-300 text-warmBrown-800 font-semibold rounded-2xl transition-colors"
              >
                Back to Shop
              </Link>
            </div>

            {/* Support */}
            <SupportBlock />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // ============================================================
  // APPROVED VIEW
  // ============================================================
  return (
    <div className="min-h-screen bg-warmWhite-100">
      <Header />
      <main className="pt-[80px] md:pt-[125px] lg:pt-[140px] pb-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-8">
              <CheckCircle2 className="w-12 h-12 text-emerald-600" />
            </div>
            <h1 className="font-display text-3xl text-warmBrown-900 mb-3">
              Thank You for Your Order!
            </h1>
            <p className="text-warmBrown-600 text-lg mb-2">
              Your payment was approved and your order is confirmed.
            </p>
            {email && (
              <p className="text-warmBrown-500 mb-8">
                A confirmation has been sent to{' '}
                <span className="font-semibold text-warmBrown-800">{email}</span>
                .
              </p>
            )}
            {!email && <div className="mb-8" />}
          </div>

          {/* Approval banner */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-3xl p-6 mb-8">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-emerald-800 mb-1">
                  Payment Approved
                </p>
                <p className="text-emerald-700 text-sm leading-relaxed">
                  Your card has been charged{' '}
                  {fmtAmount ? <strong>{fmtAmount}</strong> : 'successfully'}.
                  This charge will appear on your statement as{' '}
                  <strong>{BRAND_INFO.billingDescriptor}</strong>.
                </p>
              </div>
            </div>
          </div>

          {/* Transaction details */}
          <div className="bg-white p-6 md:p-8 rounded-3xl shadow-card border border-cream-200 mb-8">
            <div className="flex items-center gap-2 mb-5">
              <CreditCard className="w-5 h-5 text-warmBrown-500" />
              <h2 className="font-display text-lg text-warmBrown-900">
                Transaction Details
              </h2>
            </div>
            <dl className="divide-y divide-cream-100">
              {orderId && (
                <DetailRow label="Order Reference" value={orderId} mono />
              )}
              {transactionId && (
                <DetailRow
                  label="NMI Transaction ID"
                  value={transactionId}
                  mono
                />
              )}
              {authCode && authCode.trim() && (
                <DetailRow label="Authorization Code" value={authCode.trim()} mono />
              )}
              {responseCode && (
                <DetailRow
                  label="Response Code"
                  value={`${responseCode}${responseText ? ` — ${responseText}` : ''}`}
                />
              )}
              {fmtAmount && (
                <DetailRow label="Amount Charged" value={fmtAmount} bold />
              )}
              <DetailRow label="Status" value="APPROVED" pillVariant="success" />
            </dl>
          </div>

          {/* What's next */}
          <div className="bg-white p-8 rounded-3xl shadow-card border border-cream-200 mb-10">
            <h2 className="font-display text-lg text-warmBrown-900 mb-6">
              What&apos;s Next?
            </h2>

            <div className="space-y-6">
              <StatusStep
                icon={<CheckCircle2 className="w-5 h-5 text-emerald-600" />}
                iconBg="bg-emerald-100"
                title="Order Confirmed"
                description="We've received your order and will begin processing it shortly."
                done
              />
              <StatusStep
                icon={<Package className="w-5 h-5 text-warmBrown-400" />}
                iconBg="bg-cream-100"
                title="Preparing Your Order"
                description="Your sewing tools are being carefully packed."
              />
              <StatusStep
                icon={<Truck className="w-5 h-5 text-warmBrown-400" />}
                iconBg="bg-cream-100"
                title="On The Way"
                description="Your order will be shipped and tracking info will be sent to your email."
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 mb-10">
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-white border border-cream-300 hover:border-rose-300 text-warmBrown-800 font-semibold rounded-2xl transition-colors"
            >
              <Home className="w-5 h-5" />
              Return Home
            </Link>
            <Link
              to="/shop"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-2xl transition-colors shadow-lg shadow-rose-600/20 group"
            >
              Continue Shopping
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Support */}
          <SupportBlock />
        </div>
      </main>
      <Footer />
    </div>
  );
};

// --------------------------------------------------------------
// Sub-components
// --------------------------------------------------------------
const DetailRow = ({ label, value, mono, bold, pillVariant }) => {
  let valueEl;
  if (pillVariant === 'success') {
    valueEl = (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold tracking-wider">
        <CheckCircle2 className="w-3.5 h-3.5" /> {value}
      </span>
    );
  } else if (pillVariant === 'error') {
    valueEl = (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs font-bold tracking-wider">
        <XCircle className="w-3.5 h-3.5" /> {value}
      </span>
    );
  } else {
    valueEl = (
      <span
        className={[
          'text-warmBrown-900 text-sm text-right break-all',
          mono ? 'font-mono' : '',
          bold ? 'font-bold' : 'font-medium',
        ].join(' ')}
      >
        {value}
      </span>
    );
  }
  return (
    <div className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
      <dt className="text-warmBrown-500 text-sm flex-shrink-0">{label}</dt>
      <dd className="min-w-0">{valueEl}</dd>
    </div>
  );
};

const StatusStep = ({ icon, iconBg, title, description, done }) => (
  <div className="flex items-start gap-4">
    <div
      className={`w-10 h-10 ${iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}
    >
      {icon}
    </div>
    <div className="text-left">
      <p className={`font-semibold ${done ? 'text-warmBrown-900' : 'text-warmBrown-700'}`}>
        {title}
      </p>
      <p className="text-warmBrown-500 text-sm">{description}</p>
    </div>
  </div>
);

const SupportBlock = () => (
  <div className="bg-cream-50 p-6 rounded-2xl border border-cream-200">
    <p className="text-warmBrown-700 font-medium mb-3 text-center">
      Need help with your order?
    </p>
    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
      <a
        href={`mailto:${CONTACT_INFO.email}`}
        className="inline-flex items-center gap-2 text-rose-600 hover:text-rose-700 font-medium"
      >
        <Mail className="w-4 h-4" />
        {CONTACT_INFO.email}
      </a>
      <span className="hidden sm:inline text-cream-300">|</span>
      <a
        href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
        className="inline-flex items-center gap-2 text-rose-600 hover:text-rose-700 font-medium"
      >
        <Phone className="w-4 h-4" />
        {CONTACT_INFO.phone}
      </a>
    </div>
  </div>
);

export default OrderConfirmationPage;
